import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { IGetSmsTwoFactorRes } from "@evennt-tech/api-v2-external";
import { apiV2 } from "api/firebase";
import { useTwoFactorSettings } from "store/two-factor/two-factor-settings.store";
import { registerStore } from "store/utils/reset-store.utils";

type State = {
  status: IGetSmsTwoFactorRes | null;
  pendingPhone: string | null;
  recoveryCodes: string[] | null;
};

type Action = {
  fetchStatus: () => Promise<void>;
  /**
   * Resolves with the seconds the server will refuse another code for. The wait
   * escalates with each unverified resend (60s → 5m → 30m → 24h), so the caller
   * has to drive its timer from this rather than from a constant of its own.
   */
  beginSetup: (password: string | undefined, phone: string) => Promise<number>;
  verifySetup: (code: string) => Promise<void>;
  revoke: (password?: string) => Promise<void>;
  clearPendingPhone: () => void;
  clearRecoveryCodes: () => void;
};

export const useSmsTwoFactor = create(
  devtools<State & Action>(
    (set, get) => ({
      status: null,
      pendingPhone: null,
      recoveryCodes: null,

      fetchStatus: async () => {
        try {
          const status = await apiV2.smsTwoFactor.get();
          set({ status });
        } catch (error) {
          console.error("Failed to fetch SMS 2FA status:", error);
          set({ status: { configured: false, phone: null, verifiedAt: null } });
        }
      },

      beginSetup: async (password, phone) => {
        const { resendAfterSeconds } = await apiV2.smsTwoFactor.setup({
          password,
          phone,
        });
        set({ pendingPhone: phone });

        return resendAfterSeconds;
      },

      verifySetup: async (code) => {
        const { recoveryCodes } = await apiV2.smsTwoFactor.verifySetup({
          code,
        });
        set({
          pendingPhone: null,
          recoveryCodes: recoveryCodes?.length ? recoveryCodes : null,
        });
        useTwoFactorSettings.getState().setMethodConfigured("sms", true);
        await get().fetchStatus();
      },

      revoke: async (password) => {
        await apiV2.smsTwoFactor.revoke({ password });
        useTwoFactorSettings.getState().setMethodConfigured("sms", false);
        await get().fetchStatus();
      },

      clearPendingPhone: () => set({ pendingPhone: null }),
      clearRecoveryCodes: () => set({ recoveryCodes: null }),
    }),
    { name: "sms-two-factor" }
  )
);

registerStore(useSmsTwoFactor);
